/**
 * Navbar - the green bar across the top of every page
 * ---------------------------------------------------
 * What it shows changes depending on who is looking:
 *   - Signed out: Browse chores, Log in, Sign up
 *   - Student:    Browse chores, My offer, My work, bell, name
 *                 ("My offer" only while offers are switched on - they were
 *                 archived on 17 October 2026, see hooks/useFeatures.js)
 *   - Poster:     Browse chores, Available students, My chores, Post a chore,
 *                 bell, name
 *   - Moderator:  the above plus Reports and the student directory
 *
 * Nobody except a moderator gets a link to the student directory. It is a
 * browsable list of children, and no ordinary use of the site needs one.
 *
 * On phones the links collapse behind the hamburger button.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Bell, CheckCircle2, LogOut, Menu, X, ShieldAlert, MessageSquare } from 'lucide-react';

import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../hooks/useI18n';
import { useFeatures } from '../hooks/useFeatures';
import LanguageSwitch from './LanguageSwitch';
import { notificationApi, messageApi, BADGES_CHANGED } from '../services/api';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ChoreConnect';

export default function Navbar() {
  const { user, isStudent, isPoster, logout } = useAuth();
  const { t } = useI18n();
  const { offersEnabled } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Close the mobile menu whenever the page changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Keep the little number on the bell up to date.
  useEffect(() => {
    if (!user) {
      setUnread(0);
      setUnreadMessages(0);
      return undefined;
    }

    let active = true;
    const load = () => {
      notificationApi
        .unreadCount()
        .then(({ unread: count }) => {
          if (active) setUnread(count);
        })
        .catch(() => {});
      messageApi
        .unread()
        .then(({ unread: count }) => {
          if (active) setUnreadMessages(count);
        })
        .catch(() => {});
    };

    load();
    // Check again every half minute so the badge stays roughly current.
    const timer = setInterval(load, 30000);

    /**
     * ALSO CHECK THE MOMENT SOMEBODY LOOKS AT THE TAB AGAIN.
     *
     * This does more than keep the bell tidy, and that is why it is here.
     * These two requests are the only thing the site asks the server while
     * somebody sits reading a page, so they are also what NOTICES a ban that
     * has just been imposed - api.js sees the refusal and signs them out.
     *
     * Browsers slow timers right down in a tab nobody is looking at, some to
     * once a minute and some further, so on its own the half-minute check
     * cannot promise anything for a tab left in the background. Asking again
     * as soon as the tab is looked at closes that gap at the only moment it
     * matters: a banned person cannot be sitting in front of a stale screen
     * if the screen is brought up to date the instant they turn to it.
     */
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);

    // ...and immediately when something on the page says it changed a count,
    // so your own click is reflected at once rather than at the next tick.
    window.addEventListener(BADGES_CHANGED, load);

    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(BADGES_CHANGED, load);
    };
  }, [user, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="container">
        <Link to="/" className="navbar-brand">
          <CheckCircle2 size={26} aria-hidden="true" />
          {APP_NAME}
        </Link>

        <button
          type="button"
          className="navbar-toggle"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <div className={`navbar-links${menuOpen ? ' open' : ''}`}>
          <NavLink to="/marketplace" className="navbar-link">
            {t('nav.browse')}
          </NavLink>

          {isStudent && (
            <>
              {offersEnabled && (
                <NavLink to="/my-offer" className="navbar-link">
                  {t('nav.myOffer')}
                </NavLink>
              )}
              <NavLink to="/dashboard" className="navbar-link">
                {t('nav.myWork')}
              </NavLink>
            </>
          )}

          {isPoster && (
            <>
              {/* "Available students" rather than "Offers". It was "Who's
                  free" until 24 August 2026 - closer to the question a
                  poster arrives with, but it read as a puzzle rather than a
                  label, and somebody who has not used the site before cannot
                  tell what is behind it. A moderator keeps this link: it is a
                  list of what students are publishing about themselves, which
                  is exactly the kind of thing they are there to look at.

                  Since offers were archived (17 October 2026) the same tab
                  leads to the counts of students ready to work rather than
                  the list - same name, because it answers the same question
                  a poster arrives with. */}
              <NavLink to="/available-students" className="navbar-link">
                {t('nav.whosFree')}
              </NavLink>

              {/* NOT for a moderator. The moderator account is a poster
                  underneath, so it inherited "My chores" and "Post a chore"
                  - and with the two moderator links as well that is six
                  items before the bell. Neither is anything the job needs:
                  a moderator is here to read reports and look at profiles,
                  not to run their own chores.

                  Removed from the BAR, not from the site. Both pages still
                  work if they ever do post something, which keeps this a
                  tidying-up rather than a rule with an exception to
                  remember. */}
              {!user?.isModerator && (
                <>
                  <NavLink to="/dashboard" className="navbar-link">
                    {t('nav.myChores')}
                  </NavLink>
                  <NavLink to="/chores/new" className="navbar-cta">
                    {t('footer.postChore')}
                  </NavLink>
                </>
              )}
            </>
          )}

          {/* Only moderators ever see these three. Both directories are
              browsable lists of people - one of them children - so neither
              is offered to anybody else, and neither is linked from an
              ordinary screen. See routes/students.js and routes/users.js. */}
          {user?.isModerator && (
            <>
              <NavLink to="/moderation" className="navbar-link">
                <ShieldAlert size={16} aria-hidden="true" />
                {t('nav.moderation')}
              </NavLink>
              <NavLink to="/students" className="navbar-link">
                {t('nav.students')}
              </NavLink>
              <NavLink to="/users" className="navbar-link">
                {t('nav.users')}
              </NavLink>
            </>
          )}

          {user ? (
            <div className="navbar-user">
              <Link
                to="/messages"
                className="navbar-bell"
                aria-label={unreadMessages
                  ? t('nav.messagesUnread', { count: unreadMessages })
                  : t('nav.messages')}
              >
                <MessageSquare size={20} />
                {unreadMessages > 0 && (
                  <span className="badge-count">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
                )}
              </Link>

              <Link
                to="/notifications"
                className="navbar-bell"
                aria-label={unread
                  ? t('nav.notificationsUnread', { count: unread })
                  : t('nav.notifications')}
              >
                <Bell size={20} />
                {unread > 0 && <span className="badge-count">{unread > 9 ? '9+' : unread}</span>}
              </Link>

              <Link to="/settings" className="navbar-link">
                {user.firstName}
              </Link>

              <button type="button" className="navbar-link" onClick={handleLogout}>
                <LogOut size={16} aria-hidden="true" />
                {t('nav.logOut')}
              </button>

              {/* Next to the name, as asked - and it sits in the signed-out
                  branch below too, because that is where it matters most. */}
              <LanguageSwitch />
            </div>
          ) : (
            <div className="navbar-user">
              <NavLink to="/login" className="navbar-link">
                {t('nav.logIn')}
              </NavLink>
              <NavLink to="/signup" className="navbar-cta">
                {t('nav.signUp')}
              </NavLink>
              <LanguageSwitch />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
